"use client";

// The commons' tiles — one skill, and one FOLDER of skills (a package).
//
// A single skill is the frosted-wash card it has always been. A package reads
// differently at a glance, on purpose: the same tile with the sheets of the
// skills inside it standing up behind its top edge (a stack, not a card), a
// folder mark where the category sits, how many it holds, and the first few of
// their names as quiet words. Opening it walks into the folder
// (/community/skills/<owner>/<repo>); it never expands in place.

import Link from "next/link";
import BrandIcon from "../BrandIcon";
import CellAvatar from "../CellAvatar";
import { originName, type CommunitySkill, type Origin } from "../../lib/community/types";
import type { SkillPack } from "../../lib/community/packs";
import { compactCount } from "../../lib/community/uses";

/** the keeper mark on a card — a member's cell, innernet's mark, or the host's. */
export function KeeperMark({ origin, size = 18 }: { origin: Origin; size?: number }) {
  if (origin.kind === "member") return <CellAvatar name={origin.name} src={origin.avatar ?? undefined} size={size} shadow={false} />;
  if (origin.kind === "innernet") return <BrandIcon name="innernet" size={size} />;
  const host = origin.host ?? "";
  return <BrandIcon name={host.includes("github") ? "github" : "web"} size={size} />;
}

export function Arrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M2.5 9.5 9.5 2.5M4 2.5h5.5V8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** the small solid star `top` wears. */
export function Star() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <path d="M6 .9l1.5 3.3 3.6.4-2.7 2.4.8 3.5L6 8.7l-3.2 1.8.8-3.5L.9 4.6l3.6-.4L6 .9z" />
    </svg>
  );
}

/** the folder a package is — a quiet line mark, never a filled icon. */
export function FolderMark({ size = 13 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M1.8 4.2c0-.7.6-1.3 1.3-1.3h2.8c.4 0 .7.2 1 .5l.8.9h5.2c.7 0 1.3.6 1.3 1.3v6.3c0 .7-.6 1.3-1.3 1.3H3.1c-.7 0-1.3-.6-1.3-1.3V4.2z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CopyMark({ done }: { done: boolean }) {
  return done ? (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
      <path d="M3 7.3 5.8 10 11 4.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <rect x="8" y="8" width="12" height="12" rx="2.5" />
      <path d="M16 8V5.5A1.5 1.5 0 0 0 14.5 4h-9A1.5 1.5 0 0 0 4 5.5v9A1.5 1.5 0 0 0 5.5 16H8" />
    </svg>
  );
}

/** count one use, without ever holding the person up (migration 109). */
export function countUse(key: string, kind: "copy" | "open") {
  try {
    const body = JSON.stringify({ key, kind });
    if (kind === "open" && navigator.sendBeacon) navigator.sendBeacon("/api/community/use", new Blob([body], { type: "application/json" }));
    else void fetch("/api/community/use", { method: "POST", headers: { "content-type": "application/json" }, body, keepalive: true });
  } catch {
    /* a count is never worth an error */
  }
}

/** the keeper line at a tile's foot — whose it is, where it lives, who brought it. */
export function KeeperLine({
  origin,
  broughtBy,
  by,
  updated,
  memberName,
}: {
  origin: Origin;
  broughtBy?: { name: string } | null;
  by?: string;
  updated?: string;
  memberName?: string | null;
}) {
  const web = origin.kind === "web" ? origin : null;
  const me = origin.kind === "member" && !!memberName && origin.name === memberName;
  return (
    <span className="cm-card__by">
      <KeeperMark origin={origin} />
      <span className="cm-card__keeper">
        {/* a skill somebody BROUGHT is its author's: "by", never "kept by",
            and the member who carried it in is named on the same line
            (law_dual_attribution). */}
        {broughtBy ? "by" : "kept by"} <b>{me ? "you" : originName(origin)}</b>
        {web?.host ? (
          <> · <a className="cm-card__host" href={web.url} target="_blank" rel="noopener noreferrer">{web.host}</a></>
        ) : by ? (
          <span className="cm-card__author"> · by {by}</span>
        ) : null}
        {broughtBy && (
          <span className="cm-card__author"> · brought by {memberName && broughtBy.name === memberName ? "you" : broughtBy.name}</span>
        )}
      </span>
      {updated && <span className="cm-card__when"><span className="ink-dot" aria-hidden />{updated}</span>}
    </span>
  );
}

export function SkillCard({
  skill: s,
  copied,
  onCopy,
  memberName,
  enter,
  out,
  inFolder,
}: {
  skill: CommunitySkill;
  copied: boolean;
  onCopy: (s: CommunitySkill) => void;
  memberName?: string | null;
  /** its place in the entrance stagger, when it is one of the first few */
  enter?: number;
  /** a search has passed it by — it fades rather than vanishing */
  out?: boolean;
  /** shown inside its package: the folder already says whose it is */
  inFolder?: boolean;
}) {
  const ext = s.external;
  return (
    // the tile is an article; the TITLE is the link and stretches over the
    // whole tile (community.css), so the keeper line can carry a second, quiet
    // link to the source without nesting one anchor in another.
    <article
      className={enter != null ? "cm-card cm-enter" : "cm-card"}
      style={enter != null ? ({ ["--i" as string]: enter } as React.CSSProperties) : undefined}
      data-out={out ? "" : undefined}
      data-hl-target=""
    >
      <span className="cm-card__top">
        <span className="cm-card__cat">{s.category}</span>
        {s.version && <span className="cm-card__ver">v{s.version}</span>}
        {s.uses && s.uses.total > 0 && (
          <span className="cm-card__uses" title={`${s.uses.saves} saved · ${s.uses.pulls} pulled by an ai · ${s.uses.copies} copied · ${s.uses.opens} opened`}>
            <Star />
            {compactCount(s.uses.total)}
          </span>
        )}
        {ext && <span className="cm-card__ext"><Arrow /></span>}
        <button
          type="button"
          className="cm-card__copy"
          data-done={copied || undefined}
          onClick={() => onCopy(s)}
          aria-label={copied ? "link copied" : `copy the link to ${s.title}`}
          title={copied ? "copied" : "copy link. paste it into your ai"}
        >
          <CopyMark done={copied} />
        </button>
      </span>
      {ext ? (
        <a className="cm-card__t cm-card__link" href={s.href} target="_blank" rel="noopener noreferrer" onClick={() => countUse(s.key, "open")}>{s.title}</a>
      ) : (
        <Link className="cm-card__t cm-card__link" href={s.href} onClick={() => countUse(s.key, "open")}>{s.title}</Link>
      )}
      <span className="cm-card__d">{s.description}</span>
      {s.tags.length > 0 && (
        <span className="cm-card__tags">
          {s.tags.slice(0, 3).map((t) => <span key={t} className="cm-tag">{t}</span>)}
        </span>
      )}
      {inFolder ? (
        s.updated ? <span className="cm-card__by"><span className="cm-card__when"><span className="ink-dot" aria-hidden />{s.updated}</span></span> : null
      ) : (
        <KeeperLine origin={s.origin} broughtBy={s.broughtBy} by={s.by} updated={s.updated} memberName={memberName} />
      )}
    </article>
  );
}

export function PackCard({
  pack,
  members,
  onOpen,
  memberName,
  enter,
  out,
  filtered,
}: {
  pack: SkillPack;
  /** the skills of it that the current lens and search let through */
  members: CommunitySkill[];
  onOpen: (pack: SkillPack, e: React.MouseEvent) => void;
  memberName?: string | null;
  enter?: number;
  out?: boolean;
  /** a lens or search is narrowing the shelf — say how many of it answer */
  filtered?: boolean;
}) {
  const n = pack.skills.length;
  const names = (filtered ? members : pack.skills).slice(0, 4);
  const rest = (filtered ? members.length : n) - names.length;
  return (
    <article
      className={enter != null ? "cm-card cm-card--pack cm-enter" : "cm-card cm-card--pack"}
      style={{ ["--i" as string]: enter ?? 0, ["--pack-n" as string]: Math.min(n, 3) } as React.CSSProperties}
      data-out={out ? "" : undefined}
      data-hl-target=""
    >
      {/* the sheets of the skills inside, standing up behind the top edge */}
      <span className="cm-pack__sheets" aria-hidden />
      <span className="cm-card__top">
        <span className="cm-card__cat cm-pack__kind"><FolderMark /> folder</span>
        <span className="cm-card__ver">{filtered && members.length < n ? `${members.length} of ${n} skills` : `${n} skills`}</span>
        {pack.uses.total > 0 && (
          <span className="cm-card__uses" title="every skill in the folder, together">
            <Star />
            {compactCount(pack.uses.total)}
          </span>
        )}
      </span>
      <Link className="cm-card__t cm-card__link" href={`/community/skills/${pack.owner.toLowerCase()}/${pack.repo.toLowerCase()}`} onClick={(e) => onOpen(pack, e)}>
        {pack.title}
      </Link>
      <span className="cm-pack__names">
        {names.map((s) => <span key={s.key} className="cm-pack__name">{s.title}</span>)}
        {rest > 0 && <span className="cm-pack__more">+{rest}</span>}
      </span>
      <KeeperLine origin={pack.origin.kind === "web" ? { ...pack.origin, url: pack.url } : pack.origin} broughtBy={pack.broughtBy} updated={pack.updated} memberName={memberName} />
    </article>
  );
}
